'use client';

import { useIonViewWillEnter } from '@ionic/react';
import {
  type FeedbackInviteRecipient,
  Permission,
  FeedbackInviteStatus,
} from '@up2stay/up2promo-api-client';
import {
  Button,
  Card,
  CardContent,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
  previewPanelSurfaceClass,
} from '@up2stay/up2promo-ui';
import type { AxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import {
  FeedbackInviteStatusBadge,
  FeedbackModeLabel,
  FeedbackRecipientStatusBadge,
} from '@/core/components/feedback/feedback-bits';
import { FeedbackFeatureGate } from '@/core/components/feedback/feedback-feature-gate';
import { FeedbackMailPreview } from '@/core/components/feedback/feedback-mail-preview';
import { FeedbackPageBreadcrumb } from '@/core/components/feedback/feedback-page-breadcrumb';
import { FeedbackPhonePreview } from '@/core/components/feedback/feedback-phone-preview';
import {
  FEEDBACK_SECTION_CARD_CLASS,
  FEEDBACK_SECTION_CARD_CONTENT_CLASS,
} from '@/core/components/feedback/feedback-surface';
import { resolveFeedbackInviteId } from '@/core/components/feedback/resolve-feedback-invite-id';
import { ListEmptyState } from '@/core/components/layout';
import { PageHeader } from '@/core/components/page-header';
import {
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsSection } from '@/core/components/settings/settings-section';
import {
  useCancelFeedbackInvite,
  useFeedbackContacts,
  useFeedbackInvite,
  useResendFeedbackInviteRecipient,
} from '@/core/hooks/use-feedback';
import { useLocale } from '@/core/hooks/use-locale';
import { useLocations } from '@/core/hooks/use-locations';
import { useIsMobile } from '@/core/hooks/use-mobile';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePermissions } from '@/core/hooks/use-permissions';
import {
  workspaceBrandAccent,
  workspaceBrandLogoUrl,
} from '@/core/lib/workspace-brand';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { AppIonPage } from '@/core/components/app-ion-page';

type ResendErrorBody = {
  error?: string;
  resendAvailableAt?: string | null;
};

function recipientDisplay(
  recipient: FeedbackInviteRecipient,
  contact: { name?: string | null; email?: string } | undefined,
) {
  const snapshot = recipient.name?.trim() || '';
  const title =
    snapshot || contact?.name || contact?.email || recipient.contactId;
  const subtitle = snapshot || contact?.name ? (contact?.email ?? null) : null;
  return { title, subtitle };
}

function parseResendError(err: unknown): ResendErrorBody {
  const axiosError = err as AxiosError<ResendErrorBody>;
  return axiosError.response?.data ?? {};
}

function formatCountdown(ms: number): string {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  const mm = minutes.toString().padStart(2, '0');
  const ss = seconds.toString().padStart(2, '0');
  if (hours > 0) {
    return `${hours}:${mm}:${ss}`;
  }
  return `${mm}:${ss}`;
}

function RecipientResendControl({
  recipient,
  canManage,
  disabled,
  onResend,
  labels,
  dateFmt,
}: {
  recipient: FeedbackInviteRecipient;
  canManage: boolean;
  disabled: boolean;
  onResend: () => void;
  labels: {
    resend: string;
    resendHint: string;
    resendCooldown: string;
  };
  dateFmt: Intl.DateTimeFormat;
}) {
  const availableAtMs = recipient.resendAvailableAt
    ? new Date(recipient.resendAvailableAt).getTime()
    : 0;
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!availableAtMs || availableAtMs <= Date.now()) {
      return;
    }
    const tick = () => setNowMs(Date.now());
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [availableAtMs]);

  const remainingMs =
    availableAtMs > 0 ? Math.max(0, availableAtMs - nowMs) : 0;

  if (!canManage || !recipient.initialSentAt) {
    return null;
  }

  const coolingDown = remainingMs > 0;
  if (recipient.canResend === false && !coolingDown) {
    return null;
  }

  return (
    <div className="flex min-w-[10.5rem] flex-col items-stretch gap-1 sm:items-start">
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="w-full sm:w-auto"
        disabled={disabled || coolingDown || recipient.canResend === false}
        title={labels.resendHint}
        onClick={onResend}
      >
        {labels.resend}
      </Button>
      <p
        className={cn(
          'min-h-4 text-xs tabular-nums text-muted-foreground',
          !coolingDown && 'invisible',
        )}
        aria-live="polite"
        aria-hidden={!coolingDown}
      >
        {labels.resendCooldown.replace('{time}', formatCountdown(remainingMs))}
      </p>
      {coolingDown && recipient.resendAvailableAt ? (
        <span className="sr-only">
          {dateFmt.format(new Date(recipient.resendAvailableAt))}
        </span>
      ) : null}
    </div>
  );
}

export function FeedbackInviteDetailPage() {
  const routeParams = useParams<{ inviteId: string }>();
  const location = useLocation();
  const inviteId = resolveFeedbackInviteId(routeParams, location.pathname);
  const dictRoot = useDictionary();
  const dict = dictRoot.feedback;
  const locale = useLocale();
  const isMobile = useIsMobile();
  const { navigateShell } = useNavigateShell();
  const { can } = usePermissions();
  const canManage = can(Permission.FEEDBACK_MANAGE);
  const canInvite = can(Permission.FEEDBACK_INVITE);
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const { locations } = useLocations();
  const {
    data: invite,
    error,
    refetch,
  } = useFeedbackInvite(workspaceId, inviteId);
  const { data: contacts = [] } = useFeedbackContacts(workspaceId);
  const cancelInvite = useCancelFeedbackInvite(workspaceId);
  const resendRecipient = useResendFeedbackInviteRecipient(workspaceId);
  const [showPreview, setShowPreview] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);

  useIonViewWillEnter(() => {
    void refetch();
  });

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(locale === 'de' ? 'de-DE' : 'en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
    [locale],
  );

  const contactById = useMemo(() => {
    const map = new Map(contacts.map((c) => [c.id, c]));
    return map;
  }, [contacts]);

  const locationName =
    locations.find((l) => l.id === invite?.locationId)?.name ??
    invite?.locationId;

  const counts = useMemo(() => {
    const recipients = invite?.recipients ?? [];
    return {
      total: recipients.length,
      completed: recipients.filter((r) => r.status === 'completed').length,
      sent: recipients.filter((r) => r.initialSentAt).length,
    };
  }, [invite?.recipients]);

  const onCancel = async () => {
    if (!inviteId) return;
    try {
      await cancelInvite.mutateAsync(inviteId);
      toast.success(dict.detail.cancelSuccess);
      void refetch();
    } catch {
      toast.error(dict.detail.cancelFailed);
    }
  };

  const onResend = async (recipientId: string) => {
    if (!inviteId) return;
    setResendingId(recipientId);
    try {
      await resendRecipient.mutateAsync({ inviteId, recipientId });
      toast.success(dict.detail.resendSuccess);
      void refetch();
    } catch (err) {
      const body = parseResendError(err);
      if (body.error === 'feedbackInviteResendRateLimited') {
        const when = body.resendAvailableAt
          ? dateFmt.format(new Date(body.resendAvailableAt))
          : '—';
        toast.error(dict.detail.resendRateLimited.replace('{time}', when));
      } else if (body.error === 'feedbackInviteResendInFlight') {
        toast.error(dict.detail.resendInFlight);
      } else if (
        body.error === 'feedbackInviteRecipientNotResendable' ||
        body.error === 'feedbackRecipientOptedOut'
      ) {
        toast.error(dict.detail.resendNotAllowed);
      } else {
        toast.error(dict.detail.resendFailed);
      }
      void refetch();
    } finally {
      setResendingId(null);
    }
  };

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout className="overflow-x-hidden min-w-0 max-w-full md:pb-4">
          <PageHeader
            title={invite?.title ?? dict.detail.title}
            breadcrumb={
              <FeedbackPageBreadcrumb
                domainLabel={dictRoot.nav.navDomainWork}
                pageLabel={dictRoot.nav.feedbackActivity}
              />
            }
            backHref={`${shellPaths.feedbackActivity}?tab=sent`}
            actions={
              canManage &&
              invite &&
              invite.status !== FeedbackInviteStatus.CANCELLED &&
              invite.status !== FeedbackInviteStatus.COMPLETED ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={cancelInvite.isPending}
                  onClick={() => void onCancel()}
                >
                  {dict.detail.cancel}
                </Button>
              ) : null
            }
          />
          <FeedbackFeatureGate>
            {Boolean(inviteId) && !invite && !error ? (
              <Skeleton className="h-40 w-full rounded-[var(--r-card)]" />
            ) : null}
            {error ? (
              <ListEmptyState
                title={dict.detail.loadFailed}
                action={
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => void refetch()}
                  >
                    {dict.retry}
                  </Button>
                }
              />
            ) : null}
            {invite && (
              <div className="flex min-w-0 flex-col gap-4">
                <SettingsSection
                  id="feedback-invite-overview"
                  title={dict.detail.overview}
                  grouped
                  footer={dict.detail.progress
                    .replace('{sent}', String(counts.sent))
                    .replace('{completed}', String(counts.completed))
                    .replace('{total}', String(counts.total))}
                  actions={<FeedbackInviteStatusBadge status={invite.status} />}
                >
                  <SettingsGroupRows>
                    <SettingsGroupRow>
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-[13.5px] text-muted-foreground">
                          {dict.detail.location}
                        </span>
                        <span className="min-w-0 truncate text-right text-[13.5px] font-medium text-foreground">
                          {locationName}
                        </span>
                      </div>
                    </SettingsGroupRow>
                    <SettingsGroupRow separated>
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-[13.5px] text-muted-foreground">
                          {dict.detail.mode}
                        </span>
                        <span className="min-w-0 truncate text-right text-[13.5px] font-medium text-foreground">
                          <FeedbackModeLabel mode={invite.mode} />
                        </span>
                      </div>
                    </SettingsGroupRow>
                    <SettingsGroupRow separated>
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-[13.5px] text-muted-foreground">
                          {dict.wizard.subject}
                        </span>
                        <span className="min-w-0 truncate text-right text-[13.5px] font-medium text-foreground">
                          {invite.subject}
                        </span>
                      </div>
                    </SettingsGroupRow>
                    <SettingsGroupRow separated>
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-[13.5px] text-muted-foreground">
                          {dict.detail.nextSend}
                        </span>
                        <span className="min-w-0 truncate text-right text-[13.5px] font-medium text-foreground">
                          {invite.sendAt
                            ? dateFmt.format(new Date(invite.sendAt))
                            : invite.firstSentAt
                              ? dateFmt.format(new Date(invite.firstSentAt))
                              : '—'}
                        </span>
                      </div>
                    </SettingsGroupRow>
                    <SettingsGroupRow separated>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghostAccent"
                          onClick={() => setShowPreview((v) => !v)}
                        >
                          {dict.compose.preview}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            navigateShell(shellPaths.feedbackActivity)
                          }
                        >
                          {dict.detail.openInbox}
                        </Button>
                      </div>
                    </SettingsGroupRow>
                  </SettingsGroupRows>
                </SettingsSection>

                {showPreview ? (
                  <div className="grid min-w-0 gap-4 md:grid-cols-2">
                    <Card
                      className={cn(
                        'flex min-w-0 w-full flex-col',
                        FEEDBACK_SECTION_CARD_CLASS,
                        previewPanelSurfaceClass,
                      )}
                    >
                      <CardContent
                        className={cn(
                          FEEDBACK_SECTION_CARD_CONTENT_CLASS,
                          'flex min-w-0 items-center justify-center overflow-hidden pt-5 sm:pt-6',
                        )}
                      >
                        <FeedbackPhonePreview
                          title={invite.title}
                          contextLabel={invite.contextLabel}
                          workspaceName={
                            invite.brandingSnapshot?.workspaceName ??
                            workspace?.name
                          }
                          locationName={locationName}
                          accentColor={workspaceBrandAccent(
                            invite.brandingSnapshot?.accentColor,
                          )}
                          logoUrl={workspaceBrandLogoUrl(workspace?.photo)}
                          questions={invite.questions ?? []}
                          showComment={(invite.questions ?? []).length > 0}
                          showUp2promoBranding={
                            invite.brandingSnapshot?.showUp2promoBranding !==
                            false
                          }
                          className="w-full"
                          labels={{
                            overallStars: dict.preview.overallStars,
                            comment: dict.preview.comment,
                            commentPlaceholder: dict.preview.commentPlaceholder,
                            required: dict.preview.required,
                            yes: dict.preview.yes,
                            no: dict.preview.no,
                            scaleLow: dict.preview.scaleLow,
                            scaleHigh: dict.preview.scaleHigh,
                            selectPlaceholder: dict.preview.selectPlaceholder,
                            submit: dict.preview.submit,
                            previewNote: dict.preview.previewNote,
                          }}
                        />
                      </CardContent>
                    </Card>
                    <FeedbackMailPreview
                      subject={invite.subject}
                      body={invite.body}
                      sample={{
                        contactName: dict.preview.sampleContact,
                        locationName:
                          typeof locationName === 'string'
                            ? locationName
                            : dict.preview.sampleLocation,
                        workspaceName:
                          invite.brandingSnapshot?.workspaceName ??
                          workspace?.name ??
                          'up2promo',
                      }}
                      fromLabel={dict.preview.from}
                      toLabel={dict.preview.to}
                      accentColor={workspaceBrandAccent(
                        invite.brandingSnapshot?.mailAccentColor ??
                          invite.brandingSnapshot?.accentColor,
                      )}
                      logoUrl={workspaceBrandLogoUrl(workspace?.photo)}
                      ctaLabel={dict.preview.mailCta}
                      poweredByLabel={dict.preview.poweredBy}
                      showPoweredBy={
                        invite.brandingSnapshot?.showUp2promoBranding !== false
                      }
                    />
                  </div>
                ) : null}

                <SettingsSection
                  id="feedback-invite-recipients"
                  title={dict.detail.recipients}
                  grouped
                  footer={dict.detail.resendHint}
                >
                  {isMobile ? (
                    <SettingsGroupRows>
                      {invite.recipients.map((r, index) => {
                        const contact = contactById.get(r.contactId);
                        const { title, subtitle } = recipientDisplay(
                          r,
                          contact,
                        );
                        return (
                          <SettingsGroupRow key={r.id} separated={index > 0}>
                            <div className="flex flex-col gap-2">
                              <div className="flex flex-col">
                                <span className="text-[13.5px] font-semibold text-foreground">
                                  {title}
                                </span>
                                {subtitle ? (
                                  <span className="text-[12px] text-muted-foreground">
                                    {subtitle}
                                  </span>
                                ) : null}
                              </div>
                              <div className="flex flex-wrap items-center gap-2">
                                <FeedbackRecipientStatusBadge
                                  status={r.status}
                                />
                                <span className="text-[12px] text-muted-foreground">
                                  {r.initialSentAt
                                    ? dateFmt.format(new Date(r.initialSentAt))
                                    : '—'}
                                </span>
                              </div>
                              {r.lastError ? (
                                <p
                                  className="text-[12px] text-destructive"
                                  title={r.lastError}
                                >
                                  {dict.detail.deliveryError}: {r.lastError}
                                </p>
                              ) : null}
                              <RecipientResendControl
                                recipient={r}
                                canManage={canInvite}
                                disabled={
                                  resendRecipient.isPending &&
                                  resendingId === r.id
                                }
                                onResend={() => void onResend(r.id)}
                                labels={{
                                  resend: dict.detail.resend,
                                  resendHint: dict.detail.resendHint,
                                  resendCooldown: dict.detail.resendCooldown,
                                }}
                                dateFmt={dateFmt}
                              />
                            </div>
                          </SettingsGroupRow>
                        );
                      })}
                    </SettingsGroupRows>
                  ) : (
                    <div className="min-w-0 overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{dict.detail.colContact}</TableHead>
                            <TableHead>{dict.detail.colStatus}</TableHead>
                            <TableHead>{dict.detail.colSent}</TableHead>
                            <TableHead>{dict.detail.colActions}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {invite.recipients.map((r) => {
                            const contact = contactById.get(r.contactId);
                            const { title, subtitle } = recipientDisplay(
                              r,
                              contact,
                            );
                            return (
                              <TableRow key={r.id}>
                                <TableCell>
                                  <div className="flex flex-col">
                                    <span className="font-medium text-foreground">
                                      {title}
                                    </span>
                                    {subtitle ? (
                                      <span className="text-[13.5px] text-muted-foreground">
                                        {subtitle}
                                      </span>
                                    ) : null}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-col gap-1">
                                    <FeedbackRecipientStatusBadge
                                      status={r.status}
                                    />
                                    {r.lastError ? (
                                      <span
                                        className={cn(
                                          'max-w-xs text-[13.5px] text-destructive',
                                        )}
                                        title={r.lastError}
                                      >
                                        {dict.detail.deliveryError}:{' '}
                                        {r.lastError}
                                      </span>
                                    ) : null}
                                  </div>
                                </TableCell>
                                <TableCell className="text-[13.5px] text-muted-foreground">
                                  {r.initialSentAt
                                    ? dateFmt.format(new Date(r.initialSentAt))
                                    : '—'}
                                </TableCell>
                                <TableCell>
                                  <RecipientResendControl
                                    recipient={r}
                                    canManage={canInvite}
                                    disabled={
                                      resendRecipient.isPending &&
                                      resendingId === r.id
                                    }
                                    onResend={() => void onResend(r.id)}
                                    labels={{
                                      resend: dict.detail.resend,
                                      resendHint: dict.detail.resendHint,
                                      resendCooldown:
                                        dict.detail.resendCooldown,
                                    }}
                                    dateFmt={dateFmt}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </SettingsSection>
              </div>
            )}
          </FeedbackFeatureGate>
        </AppPageLayout>
      </AppPageContent>
    </AppIonPage>
  );
}
